import { useState, useEffect } from "react";
import { apiGet } from "../api/http";
import { useAuth } from "../context/AuthContext";
import AuthCallout from "../components/AuthCallout";
import ToolUrlCard from "../components/ToolUrlCard";
import { useActiveSite } from "../context/ActiveSiteContext";
import { useToast } from "../context/ToastContext";
import { isAuthError, parseApiError } from "../utils/apiErrors";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import { ScoreRing } from "../components/SeoScoreCard";

const metricKeys = [
  ["pageSpeed", "Performance"],
  ["bestPractices", "Best Practices"],
  ["firstContentfulPaint", "FCP"],
  ["largestContentfulPaint", "LCP"],
  ["totalBlockingTime", "TBT"],
  ["cumulativeLayoutShift", "CLS"],
  ["serverResponseTime", "Server Response"],
];

const demoPageSpeedReport = {
  url: "https://web.dev",
  pageSpeed: 91,
  bestPractices: 96,
  firstContentfulPaint: "0.8 s",
  largestContentfulPaint: "1.2 s",
  totalBlockingTime: "20 ms",
  cumulativeLayoutShift: "0.01",
  serverResponseTime: "120 ms",
  suggestions: [
    "Serve images in next-gen formats (WebP/AVIF)",
    "Reduce unused JavaScript and CSS",
    "Preload largest contentful paint image",
    "Eliminate render-blocking resources",
    "Properly size images to reduce CLS",
    "Minify JavaScript and CSS files",
  ],
};

export default function PageSpeed() {
  const { isAuthenticated } = useAuth();
  const { showError } = useToast();
  const { activeSite, refreshSites } = useActiveSite();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const displayReport = report || (!isAuthenticated ? demoPageSpeedReport : null);

  useEffect(() => {
    if (activeSite) {
      setUrl(activeSite.url);
    }
  }, [activeSite]);

  const runCheck = async () => {
    if (!url || !isAuthenticated) {
      return;
    }
    if (!isValidWebsiteUrl(url)) {
      showError("Please enter a valid URL.");
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);

    try {
      const { data } = await apiGet("/api/seo/run", { url });
      setReport(data);
      refreshSites();
    } catch (err) {
      const message = parseApiError(err, "Failed to run PageSpeed");
      setError(isAuthError(err) ? `${message}. Please sign in and try again.` : message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">PageSpeed / Core Web Vitals</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        Measure performance, accessibility, and best practices using Lighthouse.
      </p>

      <AuthCallout feature="PageSpeed checks" />

      <ToolUrlCard
        url={url}
        onChange={setUrl}
        onSubmit={runCheck}
        buttonLabel={loading ? "Running..." : "Run PageSpeed"}
        loading={loading}
        disabled={!isAuthenticated}
        placeholder="your-website.com"
        inputAriaLabel="Website URL for PageSpeed"
      />

      {error ? <div className="progress-error">{error}</div> : null}
      {loading ? <div className="loading">Running PageSpeed analysis...</div> : null}

      {displayReport ? (
        <>
            {!isAuthenticated ? (
              <div className="site-panel-empty" style={{ marginBottom: 24, background: 'rgba(37, 99, 235, 0.05)', border: '1px dashed var(--accent)' }}>
                Demo preview for <strong>{demoPageSpeedReport.url}</strong>. Sign in to run live checks for your own sites.
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: 32, justifyContent: 'center', marginBottom: 40, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <ScoreRing score={displayReport.pageSpeed} size={120} />
                <div style={{ marginTop: 8, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-muted)' }}>PERFORMANCE</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <ScoreRing score={displayReport.bestPractices} size={120} />
                <div style={{ marginTop: 8, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-muted)' }}>BEST PRACTICES</div>
              </div>
            </div>

            <div className="result-grid">
              {metricKeys.slice(2).map(([key, label]) => (
                <div className="result-item" key={key}>
                  <div className="result-label">{label}</div>
                  <div className="result-value">
                    {displayReport[key] || "N/A"}
                  </div>
                </div>
              ))}
            </div>

            <div className="card" style={{ backgroundColor: "#f8fafc", marginTop: 24, border: '1px solid var(--border)' }}>
              <h3 style={{ marginTop: 0, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>⚡</span> Optimization Opportunities
              </h3>
              {Array.isArray(displayReport.suggestions) && displayReport.suggestions.length > 0 ? (
                <ul className="plain-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '8px 24px' }}>
                  {displayReport.suggestions.slice(0, 10).map((item) => (
                    <li key={item} style={{ fontSize: '0.9rem', color: 'var(--text)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ color: 'var(--warning)', fontWeight: 'bold' }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="site-panel-empty">No major opportunities were flagged in the returned Lighthouse summary.</div>
              )}
            </div>
          </>
        ) : null}
    </div>
  );
}

